import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import FooterAlt from "@/components/FooterAlt";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, Clock } from "lucide-react";
import StructuredData from "@/components/StructuredData";

const EcosystemLedGrowth = () => {
  useEffect(() => {
    document.title = "Ecosystem-Led Growth Strategy: A VC's Perspective | AMX Ventures";
    const desc = "Learn how B2B SaaS companies achieve 80% CAC reduction and 3-4x faster sales cycles through hyperscaler partnerships. Expert insights from Arun Mohan, Investor at AMX Ventures.";
    let meta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", desc);
  }, []);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": "What is Ecosystem-Led Growth? A VC's Perspective",
    "description": "Ecosystem-led growth represents a fundamental shift in how B2B SaaS companies scale through hyperscaler partnerships.",
    "author": {
      "@type": "Person",
      "name": "Arun Mohan",
      "jobTitle": "Investor"
    },
    "publisher": {
      "@type": "Organization",
      "name": "AMX Ventures"
    },
    "datePublished": "2025-01-15",
    "dateModified": "2025-01-15"
  };

  return (
    <div className="min-h-screen bg-background">
      <StructuredData data={articleSchema} />
      <Navigation />

      <article className="container mx-auto px-4 py-16 max-w-4xl">
        <Button asChild variant="ghost" className="mb-8">
          <Link to="/blog">
            <ArrowLeft className="mr-2 w-4 h-4" />
            Back to Blog
          </Link>
        </Button>

        <header className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
            What is Ecosystem-Led Growth? A VC's Perspective
          </h1>
          
          <div className="flex flex-wrap items-center gap-4 text-muted-foreground mb-6">
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              January 15, 2025
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              8 min read
            </span>
            <span>By Arun Mohan</span>
          </div>
        </header>

        <div className="prose prose-lg max-w-none">
          <p className="text-xl text-muted-foreground leading-relaxed mb-8">
            After building and exiting two companies, and now investing in six more, I've learned one undeniable truth: the fastest-growing B2B SaaS companies don't just build great products—they build within ecosystems.
          </p>

          <h2 className="text-3xl font-bold mt-12 mb-4">The Traditional Approach is Broken</h2>
          <p>
            For decades, the SaaS playbook has been straightforward: build a product, hire sales teams, spend heavily on marketing, and grind your way to customers. This approach works, but it's capital-intensive, slow, and increasingly competitive.
          </p>
          <p>
            The average B2B SaaS company spends <strong>$1.16 to acquire $1 of revenue</strong> in their first year. Sales cycles stretch 6-12 months. Win rates hover around 20-25%. And you're competing against hundreds of similar solutions.
          </p>

          <h2 className="text-3xl font-bold mt-12 mb-4">Enter Ecosystem-Led Growth</h2>
          <p>
            Ecosystem-led growth flips this model. Instead of fighting for attention in a crowded market, you align with the platforms your customers already trust and use—AWS, Microsoft Azure, Google Cloud, Oracle, Salesforce.
          </p>
          <p>
            When you build <em>within</em> these ecosystems rather than just <em>on top</em> of them, three things happen:
          </p>
          <ul className="space-y-2 my-6">
            <li><strong>Instant Credibility:</strong> Being listed on AWS Marketplace or Microsoft AppSource signals you're enterprise-ready</li>
            <li><strong>Built-in Distribution:</strong> Hyperscalers have massive sales teams actively looking for solutions to recommend</li>
            <li><strong>Aligned Incentives:</strong> Cloud providers want to grow their consumption—your success drives their revenue</li>
          </ul>

          <h2 className="text-3xl font-bold mt-12 mb-4">The Data Doesn't Lie</h2>
          <p>
            Across our portfolio companies that have embraced ecosystem-led growth, we've seen:
          </p>
          <ul className="space-y-2 my-6">
            <li><strong>80% reduction in CAC</strong> compared to traditional outbound sales</li>
            <li><strong>3-4x faster sales cycles</strong> when co-selling with hyperscaler partners</li>
            <li><strong>Higher win rates (40-60%)</strong> versus industry average of 20-25%</li>
            <li><strong>Larger deal sizes</strong> due to enterprise customer access</li>
          </ul>

          <h2 className="text-3xl font-bold mt-12 mb-4">It's Not Just About Marketplaces</h2>
          <p>
            Many founders think ecosystem-led growth means listing on AWS Marketplace. That's step one, not the strategy.
          </p>
          <p>
            True ecosystem alignment means:
          </p>
          <ul className="space-y-2 my-6">
            <li>Building <strong>native integrations</strong> that make your solution feel like part of the platform</li>
            <li>Participating in <strong>co-sell programs</strong> where partner sales teams recommend you</li>
            <li>Creating <strong>joint solutions</strong> that solve customer problems neither company could address alone</li>
            <li>Earning <strong>technical validations</strong> and certifications that signal quality</li>
            <li>Engaging in <strong>joint marketing</strong> that amplifies your message</li>
          </ul>

          <h2 className="text-3xl font-bold mt-12 mb-4">The AMX Approach</h2>
          <p>
            At AMX Ventures, we don't just write checks. We bring 15+ years of experience building ecosystem-first companies. When we invest, we:
          </p>
          <ul className="space-y-2 my-6">
            <li>Connect you with the right GTM contacts at AWS, Microsoft, Google, Oracle, and Salesforce</li>
            <li>Help you design integration strategies that maximize partnership value</li>
            <li>Guide marketplace optimization and co-sell program participation</li>
            <li>Share playbooks from portfolio companies that have scaled this way</li>
          </ul>

          <h2 className="text-3xl font-bold mt-12 mb-4">Who Should Consider This Strategy?</h2>
          <p>
            Ecosystem-led growth isn't for everyone. It works best for:
          </p>
          <ul className="space-y-2 my-6">
            <li><strong>B2B SaaS companies</strong> targeting enterprise customers</li>
            <li><strong>Cloud-native solutions</strong> that complement hyperscaler platforms</li>
            <li><strong>DevOps, security, data, AI/ML tools</strong> where technical integration matters</li>
            <li><strong>Companies with strong product-market fit</strong> ready to scale GTM</li>
          </ul>

          <h2 className="text-3xl font-bold mt-12 mb-4">The Bottom Line</h2>
          <p>
            Ecosystem-led growth isn't a trend—it's the future of B2B SaaS distribution. The companies that figure this out early will scale faster, more efficiently, and more sustainably than their competitors.
          </p>
          <p>
            If you're building a B2B SaaS company and haven't thought about ecosystem strategy, you're already behind.
          </p>

          <div className="bg-secondary border-l-4 border-accent p-8 mt-12">
            <p className="text-lg font-semibold mb-2">Want to learn more about ecosystem-led growth?</p>
            <p className="text-muted-foreground mb-4">
              Schedule a meeting with our team to discuss how AMX Ventures can help you scale through strategic partnerships.
            </p>
            <Button asChild>
              <Link to="/schedule">Schedule a Meeting</Link>
            </Button>
          </div>
        </div>
      </article>

      <FooterAlt />
    </div>
  );
};

export default EcosystemLedGrowth;
